import { useMutation, UseMutationOptions } from "@tanstack/react-query";

import { authClient, betterAuthErrorText } from "lib/auth/authClient";
import { awsWafTokenHeaders } from "lib/auth/awsWafToken";
import { SKIP_SENTRY_FOR_CLIENT_ERRORS } from "lib/sentryErrors";

export type ChangeEmailVariables = {
  newEmail: string;
  callbackURL: string;
};

export type ChangeEmailResponseData = { status: boolean } | null;

/** A refused email change, carrying better-auth's error `code` when the server sent one. */
export class ChangeEmailError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
  }
}

export function useChangeEmail(
  options?: UseMutationOptions<
    ChangeEmailResponseData,
    ChangeEmailError,
    ChangeEmailVariables,
    unknown
  >,
) {
  return useMutation({
    meta: SKIP_SENTRY_FOR_CLIENT_ERRORS,
    mutationKey: ["changeEmail"],
    mutationFn: async ({
      newEmail,
      callbackURL,
    }: ChangeEmailVariables): Promise<ChangeEmailResponseData> => {
      const wafHeaders = await awsWafTokenHeaders();
      const { data, error } = await authClient.changeEmail(
        { newEmail, callbackURL },
        { headers: wafHeaders },
      );
      if (error) {
        throw new ChangeEmailError(
          betterAuthErrorText(error, "Could not initiate email change"),
          error.code,
          { cause: error },
        );
      }
      return data;
    },
    ...options,
  });
}
